/**
 * MSAL Configuration for Microsoft Entra ID Authentication
 *
 * This module configures the Microsoft Authentication Library (MSAL)
 * for use with the HireNow authentication system.
 *
 * Configuration values are loaded from environment variables to support
 * different deployment environments (development, staging, production).
 */

import type { Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Environment variable helpers with type safety
 */
const getEnvVar = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[key] as string | undefined;
  if (!value && defaultValue === undefined) {
    console.warn(`Environment variable ${key} is not set`);
    return '';
  }
  return value ?? defaultValue ?? '';
};

const isDebugEnabled = (): boolean => {
  return import.meta.env.VITE_AUTH_DEBUG === 'true';
};

/**
 * MSAL Configuration
 *
 * Defines the configuration for Microsoft Entra ID authentication.
 * See: https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications
 */
export const msalConfig: Configuration = {
  auth: {
    // Client ID from Azure App Registration
    clientId: getEnvVar('VITE_ENTRA_CLIENT_ID', '<CLIENT_ID>'),

    // Authority URL for your tenant
    authority: getEnvVar(
      'VITE_ENTRA_AUTHORITY',
      'https://login.microsoftonline.com/<TENANT_ID>'
    ),

    // Where to redirect after authentication
    redirectUri: getEnvVar('VITE_ENTRA_REDIRECT_URI', 'http://localhost:3000'),

    // Where to redirect after logout
    postLogoutRedirectUri: getEnvVar(
      'VITE_ENTRA_POST_LOGOUT_REDIRECT_URI',
      'http://localhost:3000'
    ),

    // Navigate to the original request URL after login
    navigateToLoginRequestUrl: true,
  },

  cache: {
    // Store tokens in sessionStorage for security (cleared when browser closes)
    // Use 'localStorage' for persistent sessions across browser restarts
    cacheLocation: 'sessionStorage',

    // Recommended: Set to true for IE11 / Edge compatibility
    storeAuthStateInCookie: false,
  },

  system: {
    // Log level configuration based on debug setting
    loggerOptions: {
      logLevel: isDebugEnabled() ? (3 as LogLevel) : (0 as LogLevel), // Verbose : Error
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) {
          return; // Never log PII
        }

        const timestamp = new Date().toISOString();
        const prefix = '[MSAL]';

        switch (level) {
          case 0: // Error
            console.error(`${timestamp} ${prefix} ${message}`);
            break;
          case 1: // Warning
            console.warn(`${timestamp} ${prefix} ${message}`);
            break;
          case 2: // Info
            console.info(`${timestamp} ${prefix} ${message}`);
            break;
          case 3: // Verbose
            console.debug(`${timestamp} ${prefix} ${message}`);
            break;
        }
      },
      piiLoggingEnabled: false,
    },

    // Allow async operations in callbacks
    allowNativeBroker: false,
  },
};

/**
 * Scopes required for login
 *
 * 'openid' - Required for ID tokens
 * 'profile' - Access to user profile claims
 * 'email' - Access to user email
 * 'User.Read' - Read user profile from Microsoft Graph
 */
export const loginScopes = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

/**
 * Scopes for silent token acquisition
 */
export const silentTokenScopes = {
  scopes: ['User.Read'],
};

/**
 * Application redirect URLs based on user role
 */
export const roleRedirectUrls = {
  admin: getEnvVar('VITE_ADMIN_APP_URL', 'https://admin.hirenow.com'),
  user: getEnvVar('VITE_USER_APP_URL', 'https://user.hirenow.com'),
};

/**
 * Get the appropriate redirect URL based on user roles
 * @param roles - Array of roles from the ID token
 * @returns The URL to redirect to after authentication
 */
export function getRedirectUrlForRoles(roles: string[]): string {
  // Check if user has Admin role (case-insensitive)
  const isAdmin = roles.some(
    (role) => role.toLowerCase() === 'admin'
  );

  return isAdmin ? roleRedirectUrls.admin : roleRedirectUrls.user;
}
